// src/lib/format.ts
//
// EVERY user-visible number in this app is formatted here, and nowhere else.
//
// ─── WHY THIS FILE EXISTS ───────────────────────────────────────────────────
// It replaced FOURTEEN module-level private formatters spread across ten
// screens — plus the locals that shadowed them — in a codebase where seventeen
// files called `toLocaleString` directly. They had drifted into three separate
// defects that were live in production:
//
//   1. TWO BYTE UNITS UNDER ONE LABEL. `OverviewPage` and `OrgsPage` divided by
//      1e9; `StoragePage` and `OrgDetailPage` divided by 1_073_741_824. Both
//      wrote "GB". The same organization's storage therefore differed by 7.4%
//      between the roster and its own detail page.
//   2. FIVE `usd()` HELPERS — in CostsPage, InvestorsPage, MarginPage,
//      ProjectionsPage and RedemptionsPage — with `maximumFractionDigits` of 0
//      and 2 mixed arbitrarily, so the same dollar figure gained or lost cents
//      depending on which screen you read it on.
//   3. A `pct` in StoragePage shadowed by a local `const pct` forty lines below
//      it, silently changing the rounding rule mid-file.
//
// ─── THE UNIT DECISION: BINARY, AND LABELLED AS SUCH ────────────────────────
// `organizations.storage_quota_bytes` is a binary quantity — the quotas in
// production are exact powers of two (2_199_023_255_552 is 2 TiB to the byte).
// Dividing a binary quota by 1e9 and calling the result "GB" reports a number
// the product never chose. So the base is 1024 and the labels are the IEC ones,
// which is also what `ComputePage` was already doing correctly on its own.
//
// ─── AND WHY MONEY STILL ACCEPTS A STRING ───────────────────────────────────
// It no longer has to. `rust_decimal` used to be declared without
// `serde-float`, so every `Decimal` crossed the wire as a JSON STRING while the
// TypeScript types said `number` — and the call sites called
// `.toLocaleString('es-MX', {…})` on them, which resolves to
// `String.prototype.toLocaleString`, a method that IGNORES its arguments. The
// money ladder rendered `1017.00` where it meant `USD 1,017.00`, on five
// endpoints, invisibly, because a fixture typed as a number always looked right.
//
// That is fixed at the source: the feature is on and
// `wire_shape.rs::a_decimal_is_a_json_number` fails without it. `Numeric` keeps
// `string` anyway, because the failure it guards against is silent and the
// guard is free: if that Cargo feature is ever dropped again, every figure here
// degrades to CORRECTLY FORMATTED rather than to a silently ignored options bag.

/** What an unmeasured value reads as. NEVER a zero: they are different claims. */
export const UNMEASURED = 'sin medir'

/** What an undefined DERIVED value reads as — a margin with no infra cost loaded. */
export const UNDEFINED_VALUE = 'sin definir'

/** The em dash used where a field is simply absent rather than unmeasured. */
export const ABSENT_MARK = '—'

const LOCALE = 'es-MX'

/**
 * A numeric value as it may arrive from the API.
 *
 * `string` is not defensive programming — it is the documented wire type of
 * every `rust_decimal::Decimal` field. See the header.
 */
export type Numeric = number | string | null | undefined

/**
 * Coerce a wire value to a finite number, or `null`.
 *
 * `null` propagates as `null` rather than 0, which is the single most
 * load-bearing rule in this codebase: unmeasured is not zero, and a zero in a
 * money column is a claim nobody made.
 */
export function toNumber(v: Numeric): number | null {
  if (v === null || v === undefined) return null
  // `Number('')` is 0 — an empty wire value would otherwise render as a
  // MEASURED zero, which in a money column is a claim nobody made.
  if (typeof v === 'string' && v.trim() === '') return null
  const n = typeof v === 'string' ? Number(v) : v
  return Number.isFinite(n) ? n : null
}

/** True when a wire value carries no measurement. */
export function isUnmeasured(v: Numeric): boolean {
  return toNumber(v) === null
}

// ─── Counts ─────────────────────────────────────────────────────────────────

/** A plain count or token quantity. Grouped, never abbreviated. */
export function count(v: Numeric, fallback = ABSENT_MARK): string {
  const n = toNumber(v)
  return n === null ? fallback : n.toLocaleString(LOCALE)
}

/**
 * A ledger movement, with its sign kept.
 *
 * The sign IS the information — the ledger's own convention is that negative
 * was spent and positive was credited — so it is never dropped for tidiness.
 */
export function signedCount(v: Numeric, fallback = ABSENT_MARK): string {
  const n = toNumber(v)
  if (n === null) return fallback
  return n > 0 ? `+${n.toLocaleString(LOCALE)}` : n.toLocaleString(LOCALE)
}

// ─── Money ──────────────────────────────────────────────────────────────────

export interface UsdOptions {
  /** Decimal places. Default 2. Pass 0 for a figure read at a glance. */
  dp?: number
  /** What to render when the value is unmeasured. Default `sin medir`. */
  fallback?: string
}

/**
 * Dollars.
 *
 * Accepts the string form a `Decimal` field arrives in — see the header. A
 * `null` renders as `sin medir`, never as `$0`.
 */
export function usd(v: Numeric, opts: UsdOptions = {}): string {
  const { dp = 2, fallback = UNMEASURED } = opts
  const n = toNumber(v)
  if (n === null) return fallback
  return n.toLocaleString(LOCALE, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  })
}

/**
 * A PRICE, as opposed to an amount.
 *
 * Separate because $0.05 through the whole-dollar formatter renders as
 * "USD 0", which turns a break-even divisor into a zero on screen while the
 * arithmetic behind it is fine. That defect was live in ProjectionsPage.
 */
export function usdRate(v: Numeric, fallback = UNMEASURED): string {
  const n = toNumber(v)
  if (n === null) return fallback
  return n.toLocaleString(LOCALE, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })
}

// ─── Bytes ──────────────────────────────────────────────────────────────────

const IEC = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'] as const

export interface BytesOptions {
  /**
   * Prefix with `~`. Storage in this app is a row count times a calibrated
   * coefficient: the ORDER between organizations is exact, the total carries a
   * few percent. A figure without the tilde gets quoted in a meeting as though
   * somebody ran `du`.
   */
  approx?: boolean
  /** Force a unit instead of choosing by magnitude. */
  unit?: (typeof IEC)[number]
  dp?: number
  fallback?: string
}

/** Bytes, base 1024, IEC labels. See the header for why binary. */
export function bytes(v: Numeric, opts: BytesOptions = {}): string {
  const { approx = false, unit, dp = 1, fallback = UNMEASURED } = opts
  const n = toNumber(v)
  if (n === null) return fallback

  // Clamped at BOTH ends. Without the lower clamp, any 0 < |n| < 1 gives a
  // negative exponent, indexes off the front of the array, and renders the
  // literal string "undefined" as its unit.
  const i =
    unit !== undefined
      ? IEC.indexOf(unit)
      : n === 0
        ? 0
        : Math.max(0, Math.min(IEC.length - 1, Math.floor(Math.log2(Math.abs(n)) / 10)))
  const scaled = n / 1024 ** i
  const digits = i === 0 ? 0 : dp
  const body = `${scaled.toLocaleString(LOCALE, { maximumFractionDigits: digits })} ${IEC[i]}`
  return approx ? `~${body}` : body
}

/** Estimated bytes. The tilde is not decoration — see {@link BytesOptions.approx}. */
export function bytesEst(v: Numeric, opts: Omit<BytesOptions, 'approx'> = {}): string {
  return bytes(v, { ...opts, approx: true })
}

/**
 * Mebibytes in, a human unit out. The compute tables store MiB, not bytes.
 *
 * Pass `unit` on a CHART AXIS. Auto-scaling is right in a sentence and wrong on
 * an axis, where one tick reading "512 MiB" beside another reading "2 GiB"
 * makes the bars incomparable at a glance — which is the only thing an axis is
 * for.
 */
export function mib(v: Numeric, opts: BytesOptions = {}): string {
  const n = toNumber(v)
  if (n === null) return opts.fallback ?? UNMEASURED
  return bytes(n * 1024 * 1024, opts)
}

// ─── Ratios ─────────────────────────────────────────────────────────────────

export interface PctOptions {
  dp?: number
  fallback?: string
}

/**
 * A ratio in 0…1 as a percentage.
 *
 * NEVER rounds up to a flat 100%: a share of 99.8% displayed as "100%" claims
 * the remainder is zero, and on the storage screen the remainder is the whole
 * question. The same guard applies at the bottom of the range.
 */
export function pct(v: Numeric, opts: PctOptions = {}): string {
  const { dp = 0, fallback = UNMEASURED } = opts
  const n = toNumber(v)
  if (n === null) return fallback
  const p = n * 100
  const rounded = `${p.toFixed(dp)}%`
  // The guard tests the ROUNDED text, not the raw value. Testing the raw value
  // let 99.5% through as a flat "100%" — the exact outcome this rule forbids —
  // because 99.5 is not greater than 99.5.
  if (p < 100 && Number(rounded.slice(0, -1)) >= 100) return `${floorTo(p, 1)}%`
  if (p > 0 && Number(rounded.slice(0, -1)) <= 0) return `${ceilTo(p, 1)}%`
  return rounded
}

/** Round toward zero at `d` decimals, so a share below 100% never reads as 100%. */
function floorTo(v: number, d: number): string {
  const f = 10 ** d
  return (Math.floor(v * f) / f).toFixed(d)
}

/** Round away from zero at `d` decimals, so a share above 0% never reads as 0%. */
function ceilTo(v: number, d: number): string {
  const f = 10 ** d
  return (Math.ceil(v * f) / f).toFixed(d)
}

// ─── Time ───────────────────────────────────────────────────────────────────
//
// Every one of these SLICES the ISO string rather than parsing it into a Date.
// That is deliberate and it is the existing convention: the API states its
// timezone in `meta.tz` and the panels render that instant, so re-projecting
// through the viewer's local zone would silently move a purchase into a
// different month from the invoice that recorded it.

/** `2026-09-01T18:00:00Z` -> `2026-09-01`. */
export function isoDate(v: string | null | undefined, fallback = ABSENT_MARK): string {
  return v ? v.slice(0, 10) : fallback
}

/** `2026-09-01T18:00:00Z` -> `2026-09-01 18:00`. */
export function isoMinute(v: string | null | undefined, fallback = ABSENT_MARK): string {
  return v ? v.slice(0, 16).replace('T', ' ') : fallback
}

/** A month key (`2026-08-01`) as a Spanish month and year. */
export function monthLabel(v: string | null | undefined, fallback = ABSENT_MARK): string {
  if (!v) return fallback
  return new Date(`${v.slice(0, 10)}T00:00:00Z`).toLocaleDateString(LOCALE, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/**
 * Whole days between a timestamp and `now`, or `null` when there is nothing to
 * measure from.
 *
 * `null` means "left no trace", NOT "never signed in" — this realm keeps no
 * login events, so every such figure is a lower bound. The screens say so.
 */
export function daysSince(v: string | null | undefined, now: number = Date.now()): number | null {
  if (!v) return null
  const t = new Date(v).getTime()
  return Number.isFinite(t) ? Math.floor((now - t) / 86_400_000) : null
}

/** Coarse relative time, for a log where the exact minute is beside the point. */
export function ago(v: string | null | undefined, now: number = Date.now()): string {
  if (!v) return ABSENT_MARK
  const t = new Date(v).getTime()
  if (!Number.isFinite(t)) return ABSENT_MARK
  const m = Math.floor((now - t) / 60_000)
  if (m < 1) return 'ahora'
  if (m < 60) return `hace ${m} min`
  const h = Math.floor(m / 60)
  if (h < 24) return `hace ${h} h`
  const d = Math.floor(h / 24)
  if (d < 30) return `hace ${d} d`
  const mo = Math.floor(d / 30)
  return mo < 12 ? `hace ${mo} meses` : `hace ${Math.floor(mo / 12)} años`
}

/** Hours, for the collector freshness table. */
export function hours(v: Numeric, fallback = ABSENT_MARK): string {
  const n = toNumber(v)
  if (n === null) return fallback
  return `${n.toLocaleString(LOCALE, { maximumFractionDigits: 0 })} h`
}

/**
 * A calibration value.
 *
 * `internal.v_calibration` unions six price tables, so one column carries token
 * costs (small integers), dollar rates ($0.05), and storage coefficients
 * (thousands of bytes). No currency symbol, because only some rows are money —
 * `revenue_rates` is dollars and `token_cost_config` is tokens, and stamping a
 * `$` on both is the kind of quiet category error this app exists to avoid.
 * It arrives as a `Decimal` string, hence {@link Numeric}.
 */
export function calibrationValue(v: Numeric, fallback = UNMEASURED): string {
  const n = toNumber(v)
  if (n === null) return fallback
  return n.toLocaleString(LOCALE, { maximumFractionDigits: 6 })
}
